import {
	Box,
	Button,
	Col,
	Component,
	Container,
	Divider,
	Dropdown,
	Grid,
	Heading,
	Icons,
	Inline,
	joinAttributes,
	Select,
	Spinner,
	Stack,
	Table,
	TableDataCell,
	Text,
	useBreakpoints,
	useId,
} from "@sembark-travel/ui-base"
import {
	arrayMutators,
	FieldArray,
	Form,
	GetFieldValue,
	isTruthy,
	SelectField,
	SubmissionError,
	SwitchInputField,
	TextInputField,
	validateFormValues,
	withServerErrors,
} from "@sembark-travel/ui-form"
import { useXHR } from "@sembark-travel/xhr"
import { IRole, SelectRoles } from "../Roles"
import { IUser } from "./../Auth"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import useSWR from "swr"
import { PERMISSIONS } from "../Auth"
import { Dialog, useDialog } from "@sembark-travel/ui-dialog"
import { SelectTeams, TTeam } from "../Teams"
import {
	AutoDistributionXHR,
	isMouseEvent,
	TActiveBasedRuleSet,
	TAnalysisParameters,
	TAppliedRules,
	TRuleSet,
	useDragDrop,
} from "./store"
import { useCallback, useEffect, useMemo, useState } from "react"
import * as Validator from "yup"
import { SelectUsers } from "../Users"
import { tripDestinationListXHR } from "../TripDestinations"
import { ActiveUserDistributionPreferencesInputField } from "./ActiveUserDistributionPreferencesInputField"
import { GroupRuleSetField } from "./GroupRuleSetField"
import { extractRightOperandName } from "./utils"

export type TDistributionPreferenceFormData = {
	enabled?: 0 | 1
	preferences: {
		included_roles?: Array<IRole>
		excluded_users?: Array<IUser>
		destination_distribution_fallback?: 0 | 1
		active_user_distribution_preferences: {
			enabled: boolean
			rule_set?: Array<TActiveBasedRuleSet>
			always_active_members?: Partial<IUser>[]
		}
	}
}

type TRuleSetForm = {
	name: string
	grouped_rule_items: TRuleSet[][]
	teams: TTeam[] | undefined
	users: IUser[] | undefined
}

function AddRuleGroupButton({
	title,
	editableData,
	onSucces,
	status,
	level,
	isEditing,
}: {
	title?: string
	editableData?: TAppliedRules["data"][0]
	onSucces: () => void
	status?: React.ComponentProps<typeof Button>["status"]
	level?: React.ComponentProps<typeof Button>["level"]
	isEditing?: boolean
}) {
	const [isOpen, openDialog, closeDialog] = useDialog()
	return (
		<>
			<Button
				onClick={() => {
					openDialog()
				}}
				status={status}
				level={level}
			>
				<Inline alignItems={"center"} gap={"1"}>
					{isEditing ? <Icons.Pencil /> : <Icons.Plus />}{" "}
					{title || "Add Rule Set"}
				</Inline>
			</Button>

			<AddEditRuleSetDialogBox
				open={isOpen}
				onCancel={() => {
					closeDialog()
				}}
				onSucces={() => {
					onSucces()
					closeDialog()
				}}
				editableData={editableData}
			/>
		</>
	)
}

interface SectionCardProps {
	title: string
	description: string
	children: React.ReactNode
}

export function SectionCard({
	title,
	description,
	children,
}: SectionCardProps) {
	return (
		<Grid rounded="md">
			<Col xs={12} md={3} bgColor="subtle" paddingY="4">
				<Stack gap="1">
					<Inline gap="3" alignItems="center">
						<Heading as="h3" fontSize="lg" fontWeight="semibold">
							{title}
						</Heading>
					</Inline>
					<Text color="muted" fontSize="sm">
						{description}
					</Text>
				</Stack>
			</Col>
			<Col style={{ paddingInline: "0px" }} xs={12} md={9}>
				{children}
			</Col>
		</Grid>
	)
}

export function AutoDistribution() {
	const xhr = useXHR()
	const id = useId()
	const { xs, md, sm } = useBreakpoints()
	const {
		data,
		mutate: distributionPreferenceApiRecall,
		isLoading: distributionPreferenceApiLoading,
	} = useSWR<{
		data: TDistributionPreferenceFormData | null
	}>(`api/trip-plan-request-distribution-preference?${id}`, () =>
		xhr
			.get("/trip-plan-request-distribution-preference")
			.then((resp) => resp.data)
	)
	const {
		data: appliedRules,
		mutate,
		isLoading,
	} = useSWR(`/routing-rules/get`, () =>
		AutoDistributionXHR(xhr).getAppliedRules({})
	)

	const { data: tripDestinationData } = useSWR("/trip-destinations", () =>
		tripDestinationListXHR(xhr).get({ include: "with_teams", has_teams: "1" })
	)

	if (distributionPreferenceApiLoading) {
		return <Spinner alignCenter padding="4" />
	}
	const initialValues = data?.data
		? {
				enabled: 1 as const,
				preferences: data.data.preferences,
			}
		: {
				enabled: 0 as const,
				preferences: {
					included_roles: [],
					excluded_users: [],
					destination_distribution_fallback: 0 as const,
					active_user_distribution_preferences: {
						enabled: false,
						rule_set: [],
						excluded_user: [],
					},
				},
			}

	return (
		<Container>
			<Stack gap="6">
				<Stack gap="2">
					<Heading fontSize={"2xl"}>Auto Distribution Preferences</Heading>
					<Text>
						Define how new trip plan requests should be auto distributed between
						your team members.
					</Text>
				</Stack>
				<Form<TDistributionPreferenceFormData>
					initialValues={initialValues}
					onSubmit={withServerErrors(async (values) => {
						if (
							isTruthy(values.enabled) &&
							!values.preferences?.included_roles?.length
						) {
							throw new Error("Please select atleast one role")
						}

						const activeDistributionPreferences =
							values.preferences?.active_user_distribution_preferences

						await xhr.post("/trip-plan-request-distribution-preference", {
							...values,
							preferences: !isTruthy(values.enabled)
								? null
								: {
										included_roles: values.preferences?.included_roles?.map(
											(role) => role.id
										),
										excluded_users: values.preferences?.excluded_users?.map(
											(user) => user.id
										),
										destination_distribution_fallback: values.preferences
											?.destination_distribution_fallback
											? 1
											: 0,
										active_user_distribution_preferences:
											activeDistributionPreferences?.enabled
												? {
														...activeDistributionPreferences,
														rule_set:
															activeDistributionPreferences.rule_set?.map(
																(item) => ({
																	...item,
																	included_user:
																		item.included_user?.map(({ id }) => id) ??
																		[],
																	included_team:
																		item.included_team?.map(({ id }) => id) ??
																		[],
																	all_users_included: Number(
																		item.all_users_included
																	),
																})
															),
														always_active_members:
															activeDistributionPreferences.always_active_members?.map(
																(user) => user.id
															),
														enabled: 1,
													}
												: undefined,
									},
						})

						if (!values.enabled) {
							distributionPreferenceApiRecall()
						}
					})}
					subscription={{ submitting: true }}
					mutators={{ ...arrayMutators }}
				>
					{({ handleSubmit, form }) => (
						<form noValidate onSubmit={handleSubmit}>
							<Stack gap={"6"}>
								<SectionCard
									title="Auto Distribution Preference"
									description="Auto-assign leads using set preferences for balanced and
											optimized allocation."
									children={
										<Stack
											width={"full"}
											justifyContent={"center"}
											bgColor={"default"}
											padding="4"
											rounded="md"
										>
											<SwitchInputField
												name="enabled"
												label="Enable Auto Distribution"
												help="Define how new trip plan requests should be auto distributed between your team members."
												onChange={(value) => {
													form.change("enabled", value)
													form.submit()
												}}
											/>
											<Divider sm />
											<GetFieldValue name="enabled">
												{({ value: enabled }) =>
													enabled ? (
														<Inline flexWrap={"wrap"} gap={"4"}>
															<Box>
																<SelectField
																	select={SelectRoles}
																	name="preferences.included_roles"
																	label="Users having role(s)"
																	multiple
																	fetchOnMount
																	onChange={(value, name) => {
																		form.change(name as never, value as never)
																		form.submit()
																	}}
																	permissions={[
																		PERMISSIONS.VIEW_TRIP_PLAN_REQUESTS,
																	]}
																/>
																<SubmissionError />
															</Box>
															<SelectField
																select={SelectUsers}
																name="preferences.excluded_users"
																label="Excluding Users"
																multiple
																onChange={(value, name) => {
																	form.change(name as never, value as never)
																	form.submit()
																}}
															/>
														</Inline>
													) : null
												}
											</GetFieldValue>
										</Stack>
									}
								/>
								<GetFieldValue name="enabled">
									{({ value: enabled }) =>
										enabled ? (
											<>
												<SectionCard
													title="Distribution Rules"
													description="Set rules to automatically distribute new trip
															requests among your team members."
													children={
														isLoading ? (
															<Spinner padding="4" alignCenter />
														) : appliedRules?.data.length ? (
															<Stack
																padding={"4"}
																width={"full"}
																justifyContent={"center"}
																bgColor={"default"}
																gap="4"
															>
																<Inline gap={"2"} justifyContent={"end"}>
																	{md || xs || sm ? (
																		<Dropdown alignRight>
																			<Dropdown.ToggleButton
																				level="tertiary"
																				size="sm"
																			>
																				<Icons.DotsVertical />
																			</Dropdown.ToggleButton>
																			<Dropdown.Menu alignItems={"start"}>
																				<Component<
																					| "ruleSet"
																					| "re-arrangement"
																					| undefined
																				>
																					initialState={undefined}
																				>
																					{({ state, setState }) => (
																						<>
																							{isMouseEvent ? (
																								<Dropdown.MenuItem>
																									<Button
																										onClick={() =>
																											setState("re-arrangement")
																										}
																									>
																										Re-arrangement
																									</Button>
																								</Dropdown.MenuItem>
																							) : null}
																							<Dropdown.MenuItem>
																								<Button
																									onClick={() =>
																										setState("ruleSet")
																									}
																								>
																									Test Rule Sets
																								</Button>
																							</Dropdown.MenuItem>

																							<TestRuleSetDialogBox
																								onCancel={() =>
																									setState(undefined)
																								}
																								open={state === "ruleSet"}
																							/>
																							<RulesSetReorderDailogBox
																								onCancel={() =>
																									setState(undefined)
																								}
																								open={
																									state === "re-arrangement"
																								}
																								onSucces={mutate}
																								editableData={appliedRules}
																							/>
																						</>
																					)}
																				</Component>
																				<Dropdown.MenuItem>
																					<AddRuleGroupButton
																						onSucces={() => {
																							mutate()
																						}}
																					/>
																				</Dropdown.MenuItem>
																			</Dropdown.Menu>
																		</Dropdown>
																	) : (
																		<>
																			<Component<
																				"ruleSet" | "re-arrangement" | undefined
																			>
																				initialState={undefined}
																			>
																				{({ state, setState }) => (
																					<>
																						<Inline gap="2">
																							{isMouseEvent ? (
																								<Button
																									onClick={() =>
																										setState("re-arrangement")
																									}
																									status="primary"
																								>
																									Re-arrangement
																								</Button>
																							) : null}
																							<Button
																								onClick={() =>
																									setState("ruleSet")
																								}
																								status="primary"
																							>
																								Test Rule Sets
																							</Button>
																						</Inline>
																						<TestRuleSetDialogBox
																							onCancel={() =>
																								setState(undefined)
																							}
																							open={state === "ruleSet"}
																						/>
																						<RulesSetReorderDailogBox
																							onCancel={() =>
																								setState(undefined)
																							}
																							open={state === "re-arrangement"}
																							onSucces={mutate}
																							editableData={appliedRules}
																						/>
																					</>
																				)}
																			</Component>
																			<AddRuleGroupButton
																				onSucces={() => {
																					mutate()
																				}}
																				status="primary"
																				level="primary"
																			/>
																		</>
																	)}
																</Inline>
																<DistributionRulesSetTable
																	refresh={mutate}
																	appliedRules={appliedRules}
																/>
															</Stack>
														) : (
															<Stack
																padding={"4"}
																width={"full"}
																justifyContent={"center"}
																bgColor={"default"}
															>
																<Stack alignItems={"center"} gap="2">
																	<Icons.Info size={"20"} color={"primary"} />
																	<Text>
																		Create your first rule set to automate lead
																		assignment
																	</Text>
																	<AddRuleGroupButton
																		onSucces={() => {
																			mutate()
																		}}
																		status="primary"
																		level="primary"
																	/>
																</Stack>
															</Stack>
														)
													}
												/>
												<SectionCard
													title="Destination wise Distributions"
													description="Assign incoming trip requests to specific Persons
															based on the selected destination."
													children={
														<Stack
															padding={"4"}
															width={"full"}
															justifyContent={"center"}
															bgColor={"default"}
															gap="4"
														>
															<Box
																borderColor={"default"}
																paddingBottom={"8"}
																paddingX={"2"}
																borderWidth={"1"}
																paddingTop={"2"}
																rounded="md"
															>
																<SwitchInputField
																	name="preferences.destination_distribution_fallback"
																	label="Enable Destination Wise"
																	onChange={(value) => {
																		form.change(
																			"preferences.destination_distribution_fallback" as keyof TDistributionPreferenceFormData,
																			value as never
																		)
																		form.submit()
																	}}
																	help="Distributes leads automatically by destination, assigning them to the relevant team member if rule conditions are not met"
																/>
															</Box>
															<GetFieldValue name="preferences.destination_distribution_fallback">
																{({ value: enableDestinationDistribution }) => {
																	return enableDestinationDistribution ? (
																		<Table
																			bordered
																			striped
																			responsive
																			headers={[
																				"Destination ",
																				"Team Name",
																				"Team Members",
																				"No. Of Members",
																			]}
																			rows={tripDestinationData?.data.map(
																				(trip) => {
																					const teamNames: string[] = []
																					let userNames: string[] = []

																					trip.teams?.forEach((team) => {
																						teamNames.push(team.name)
																						team.users?.forEach((user) => {
																							userNames.push(user.name)
																						})
																					})
																					userNames = [...new Set(userNames)]

																					return [
																						trip.name,
																						teamNames.join(","),
																						joinAttributes(...userNames),
																						userNames.length,
																					]
																				}
																			)}
																		/>
																	) : null
																}}
															</GetFieldValue>
														</Stack>
													}
												/>

												<ActiveUserDistributionPreferencesInputField
													name={
														"preferences.active_user_distribution_preferences"
													}
													onSaveRuleSet={async (editingIndex) => {
														await form.submit()
														const hasNoError = !form.getState().hasSubmitErrors
														if (hasNoError) {
															showSnackbar(
																editingIndex === null
																	? "Rule Added Successfully!"
																	: "Rule Updated Successfully!"
															)
														}
														return hasNoError
													}}
													onChange={async () => {
														await form.submit()
														const hasNoError = !form.getState().hasSubmitErrors
														if (hasNoError) {
															showSnackbar("Data Updated Successfully!", {
																id: "updated_users",
															})
														}
														return hasNoError
													}}
													deleteDeviceRule={async (updatedRuleSets) => {
														await form.submit()
														const hasNoError = !form.getState().hasSubmitErrors
														if (hasNoError) {
															showSnackbar("Rule Deleted Successfully!")
														}
														return hasNoError
													}}
												/>
											</>
										) : null
									}
								</GetFieldValue>
							</Stack>
						</form>
					)}
				</Form>
			</Stack>
		</Container>
	)
}

function AddEditRuleSetDialogBox({
	onSucces,
	onCancel,
	open,
	editableData,
}: {
	open: boolean
	onCancel: () => void
	onSucces: () => void
	editableData?: TAppliedRules["data"][0]
}) {
	return (
		<Dialog
			open={open}
			onClose={onCancel}
			title={
				<Stack gap={"2"}>
					<Heading fontSize={"lg"}>
						{editableData ? "Edit Rule Set" : "Create New Rule Set"}
					</Heading>
					<Text fontSize={"sm"} fontWeight="normal" color={"muted"}>
						Define conditions and assign leads to specific team members
					</Text>
				</Stack>
			}
		>
			<Dialog.Body>
				<RuleSetsForm
					onCancel={onCancel}
					editableData={editableData}
					onSucces={onSucces}
				/>
			</Dialog.Body>
		</Dialog>
	)
}

const RuleSetsFormSchema = validateFormValues(
	Validator.object().shape({
		name: Validator.string().required("Rule name is required"),
		grouped_rule_items: Validator.array()
			.required("Rules are required")
			.min(1, "Select at least one rule")
			.of(
				Validator.array()
					.required("Sub Rules are required")
					.min(1, "Select at least one sub rule")
					.of(
						Validator.object().shape({
							left_operand_name: Validator.mixed()
								.nullable()
								.required("Condition  is required"),
							operator: Validator.mixed()
								.nullable()
								.required("Operator is required"),
							right_operand_value: Validator.mixed()
								.nullable()
								.required("Value is required"),
						})
					)
			),
		users: Validator.array()
			.of(Validator.object().required())
			.test(
				"users-or-teams",
				"Select at least one user or one team",
				function (users) {
					const { teams } = this.parent
					return (users && users.length > 0) || (teams && teams.length > 0)
				}
			),

		teams: Validator.array()
			.of(Validator.object().required())
			.test(
				"teams-or-users",
				"Select at least one team or one user",
				function (teams) {
					const { users } = this.parent
					return (teams && teams.length > 0) || (users && users.length > 0)
				}
			),
	})
)

function RuleSetsForm({
	onSucces,
	editableData,
	onCancel,
}: {
	onSucces: () => void
	editableData?: TAppliedRules["data"][0]
	onCancel: () => void
}) {
	const xhr = useXHR()

	const initialValuesRuleSet: TRuleSetForm = useMemo(() => {
		return {
			name: "",
			grouped_rule_items: [
				[
					{
						left_operand_name: null,
						operator: null,
						right_operand_value: null,
						relation: "AND",
						type: "string",
					},
				],
			],
			users: [],
			teams: [],
		}
	}, [])

	const RuleSetFormValueInitialValues: TRuleSetForm = useMemo(() => {
		return editableData
			? {
					name: editableData?.name || "",
					grouped_rule_items:
						editableData?.grouped_rule_items?.map((ruleGroup) =>
							ruleGroup.map((rule, i, t) => {
								const { operator, ...rest } = rule.parameter
								return {
									left_operand_name: {
										...rest,
										name: rest.name,
									},
									operator,
									right_operand_value: rule.right_operand_value,
									relation: i === t.length - 1 ? "AND" : "OR",
									type: "string",
								}
							})
						) || [],
					users: editableData?.users || [],
					teams: editableData?.teams || [],
				}
			: initialValuesRuleSet
	}, [editableData, initialValuesRuleSet])

	return (
		<Form<TRuleSetForm>
			initialValues={RuleSetFormValueInitialValues}
			validate={RuleSetsFormSchema}
			onSubmit={withServerErrors(async (values) => {
				const grouped_rule_items = values.grouped_rule_items.map((group) =>
					group.map((rule) => {
						return {
							left_operand_name: rule.left_operand_name?.name,
							right_operand_value: extractRightOperandName(
								rule.right_operand_value
							),
							operator: rule.operator?.name,
						}
					})
				)
				if (editableData) {
					await xhr.patch(`/routing-rules/${editableData.id}`, {
						name: values.name,
						grouped_rule_items,
						teams: values.teams?.map((team) => team?.id),
						users: values.users?.map((user) => user?.id),
					})
				} else {
					await xhr.post("/routing-rules", {
						name: values.name,
						grouped_rule_items,
						teams: values.teams?.map((team) => team?.id),
						users: values.users?.map((user) => user?.id),
					})
				}
				showSnackbar("Preferences updated.")
				onSucces()
			})}
			subscription={{ submitting: true }}
			mutators={{
				...arrayMutators,
			}}
		>
			{({ handleSubmit, submitting, form }) => (
				<form noValidate onSubmit={handleSubmit}>
					<Container>
						<Stack gap={"4"}>
							<TextInputField
								label={"Enter Rule Name"}
								name="name"
								type="text"
								required
							/>
							<GroupRuleSetField />
							<Inline gap={"4"}>
								<SelectField
									select={SelectUsers}
									name="users"
									label="User Assignees"
									multiple
									fetchOnMount
								/>
								<SelectField
									select={SelectTeams}
									name="teams"
									label="Team Assignees"
									multiple
									fetchOnMount
								/>
							</Inline>
							<GetFieldValue<IUser[]> name="users">
								{({ value: users }) => (
									<GetFieldValue<TTeam[]> name="teams">
										{({ value: teams }) => (
											<AssignedMessage teams={teams} users={users} />
										)}
									</GetFieldValue>
								)}
							</GetFieldValue>
						</Stack>
					</Container>

					<Divider />
					<Stack gap={"4"} alignItems={"end"}>
						<SubmissionError />
						<Inline gap={"4"}>
							<Button disabled={submitting} onClick={onCancel}>
								Cancel
							</Button>
							<Button
								disabled={submitting}
								type="submit"
								status="primary"
								level="primary"
							>
								{submitting ? "Saving..." : "Save Rule Set"}
							</Button>
						</Inline>
					</Stack>
				</form>
			)}
		</Form>
	)
}

function AssignedMessage({ teams, users }: { teams: TTeam[]; users: IUser[] }) {
	const teamNames = useMemo(() => teams.map((t) => t.name).join(", "), [teams])
	const userNames = useMemo(() => users.map((u) => u.name).join(", "), [users])

	const assignedTo = useMemo(() => {
		if (teamNames && userNames) {
			return `anyone from ${teamNames} or to ${userNames}`
		} else if (teamNames) {
			return `anyone from ${teamNames}`
		} else if (userNames) {
			return userNames
		}
		return null
	}, [teamNames, userNames])

	if (!assignedTo) {
		return null
	}
	return (
		<Inline gap={"2"}>
			<Box>
				<Icons.Info />
			</Box>
			<Text>This lead will be assigned to {assignedTo}.</Text>
		</Inline>
	)
}

type TTestRuleSetForm = {
	data: { parameter: TAnalysisParameters["data"][0] | null; value: string }[]
}
function TestRuleSetDialogBox({
	onCancel,
	open,
}: {
	open: boolean
	onCancel: () => void
}) {
	return (
		<Dialog
			open={open}
			onClose={onCancel}
			title={
				<Stack gap={"2"}>
					<Heading fontSize={"lg"}>Test Rule Sets</Heading>
					<Text fontSize={"sm"} fontWeight="normal" color={"muted"}>
						Test your rules with a sample inquiry to see which rule matches and
						who gets assigned
					</Text>
				</Stack>
			}
			sm
		>
			<Dialog.Body>
				<TestRuleSetAnalysis onCancel={onCancel} />
			</Dialog.Body>
		</Dialog>
	)
}

const schemaTestRuleSetAnalysis = validateFormValues(
	Validator.object({
		data: Validator.array()
			.of(
				Validator.object().shape({
					parameter: Validator.mixed().required("Parameter is required"),
					value: Validator.string().required("Value is required"),
				})
			)
			.min(1, "At least one parameter is required"),
	})
)

function TestRuleSetAnalysis({ onCancel }: { onCancel: () => void }) {
	const xhr = useXHR()
	type TRulesAnalysis = {
		data: {
			description: string
			assignees: IUser[] | null
		}
	}
	const [assigneeMessage, setAssigneeMessage] = useState<
		TRulesAnalysis["data"] | null
	>(null)

	const { data: listofParameters } = useSWR(
		`/routing-rules-analysis/parameters`,
		() =>
			AutoDistributionXHR(xhr)
				.getAnalysisParameters()
				.then((resp) => resp.data)
	)
	return (
		<Form<TTestRuleSetForm>
			validate={schemaTestRuleSetAnalysis}
			initialValues={{ data: [{ value: "", parameter: null }] }}
			onSubmit={withServerErrors(async (values) => {
				setAssigneeMessage(null)
				const payload = {
					data: values?.data.map((v) => {
						return {
							parameter: v.parameter?.name,
							value: v.value,
						}
					}),
				}
				const resp = await xhr.post<TRulesAnalysis>(
					`routing-rules-analysis`,
					payload
				)
				setAssigneeMessage(resp.data.data)
			})}
			subscription={{
				submitting: true,
			}}
			mutators={{
				...arrayMutators,
			}}
		>
			{({ submitting, handleSubmit }) => (
				<form noValidate onSubmit={handleSubmit}>
					<FieldArray name={"data"}>
						{({ fields }) => (
							<Stack gap={"4"}>
								{fields.map((name, index) => (
									<Inline key={name} flexWrap={"wrap"} gap={"4"}>
										<SelectField
											select={Select}
											name={`${name}.parameter`}
											label="Parameter"
											labelKey="display_name"
											width={"auto"}
											options={listofParameters}
										/>
										<Inline gap={"2"} justifyContent={"center"}>
											<GetFieldValue<TAnalysisParameters["data"][0]>
												name={`${name}.parameter`}
											>
												{({ value: parameter }) => {
													return parameter.values ? (
														<SelectField
															select={Select}
															name={`${name}.value`}
															required
															creatable
															label="Value"
															placeholder="Select a value"
															options={parameter.values}
															width={"auto"}
														/>
													) : (
														<TextInputField
															label={"Value"}
															placeholder="Type Value"
															name={`${name}.value`}
															required
														/>
													)
												}}
											</GetFieldValue>

											{(fields.length || 0) > 1 ? (
												<Stack justifyContent={"end"}>
													<Box
														onClick={() => {
															fields.remove(index)
														}}
														cursor="pointer"
													>
														<Icons.Cancel />
													</Box>
												</Stack>
											) : null}
										</Inline>
									</Inline>
								))}
								<Box>
									<Button
										onClick={() => {
											const obj = {
												parameter: null,
												value: "",
											}

											fields.push(obj)
										}}
										status="primary"
									>
										<Inline alignItems={"center"}>
											<Icons.Plus /> Add More
										</Inline>
									</Button>
								</Box>
							</Stack>
						)}
					</FieldArray>

					<Divider sm />
					<Stack gap="2">
						{assigneeMessage ? (
							<Stack
								padding={"2"}
								color={assigneeMessage?.assignees ? "success" : "danger"}
								bgColor={assigneeMessage?.assignees ? "success" : "danger"}
								borderWidth={"1"}
								borderColor={assigneeMessage?.assignees ? "success" : "danger"}
								rounded="md"
								flexWrap={"wrap"}
								justifyContent={"start"}
							>
								<Stack gap={"2"}>
									<Text fontWeight="semibold" fontSize={"md"}>
										{assigneeMessage.assignees
											? "Match Found"
											: "Match Not Found"}
									</Text>

									{assigneeMessage.assignees ? (
										<Inline gap="1">
											<Text
												wordBreak="words"
												fontSize={"base"}
												fontWeight="semibold"
											>
												Assignees:
											</Text>
											<Text
												wordBreak="words"
												fontSize={"base"}
												fontWeight="normal"
											>
												{assigneeMessage.assignees
													?.map((user) => ` ${user.name}`)
													.join(", ")}
												.
											</Text>
										</Inline>
									) : null}
								</Stack>
								<Stack>
									<Divider style={{ margin: "6px 0px" }} sm />
									<Inline gap="2" alignItems={"center"}>
										<Icons.Info />
										<Text>{assigneeMessage.description}</Text>
									</Inline>
								</Stack>
							</Stack>
						) : null}
						<Stack gap={"4"}>
							<SubmissionError />
							<Inline justifyContent={"end"} gap={"4"}>
								<Button
									onClick={() => {
										onCancel()
									}}
									type="button"
								>
									Cancel
								</Button>
								<Button status="primary" disabled={submitting} type="submit">
									{submitting
										? "Testing..."
										: assigneeMessage
											? "Test Again"
											: "Test"}
								</Button>
							</Inline>
						</Stack>
					</Stack>
				</form>
			)}
		</Form>
	)
}

function DistributionRulesSetTable({
	appliedRules: data,
	refresh,
	onCancel,
	isDrag,
}: {
	appliedRules: TAppliedRules
	refresh: () => void
	onCancel?: () => void
	isDrag?: boolean
}) {
	const xhr = useXHR()
	const makeDataPriorityWise = useCallback(function (
		values: TAppliedRules["data"] | undefined
	) {
		return (values || []).sort((a, b) => a.priority - b.priority)
	}, [])
	const [appliedRules, setAppliedRules] = useState(
		makeDataPriorityWise(data.data)
	)

	useEffect(() => {
		if (data.data) {
			setAppliedRules(makeDataPriorityWise(data.data))
		}
	}, [data, makeDataPriorityWise])

	const {
		handleDragStart,
		handleDragOver,
		handleDrop,
		getRowStyle,
		handleDragLeave,
	} = useDragDrop<TAppliedRules["data"][0]>(appliedRules, (ids) => {
		setAppliedRules(ids)
	})

	async function handelSubmit() {
		const priorityData = appliedRules.map(({ id }, i) => ({ id, priority: i }))
		try {
			await xhr.patch("/routing-rules-priorities", { rules: priorityData })
			refresh()
			if (onCancel) {
				onCancel()
			}
		} catch (err) {
			const error = err as Error
			window.alert(
				error?.message ||
					"Sorry! Something went wrong. Please try again after some time."
			)
			setAppliedRules(makeDataPriorityWise(data.data))
		}
	}

	return (
		<Stack gap="2">
			<Table
				bordered
				striped={!isDrag}
				responsive
				headers={["Rule Set Name", "Conditions", "Assigned To"].concat(
					isDrag ? [] : [" "]
				)}
				alignCols={{ 3: "right" }}
				children={
					<tbody>
						{appliedRules.map((ruleGroup, r) => (
							<Box
								style={{
									...getRowStyle(r),
									cursor: isDrag ? "grab" : "default",
								}}
								as="tr"
								key={ruleGroup.id}
								draggable={!!isDrag}
								onDragStart={
									isDrag
										? (e: React.DragEvent<HTMLTableRowElement>) =>
												handleDragStart(e, r)
										: undefined
								}
								onDrop={
									isDrag
										? (e: React.DragEvent<HTMLTableRowElement>) =>
												handleDrop(e, r)
										: undefined
								}
								onDragOver={
									isDrag
										? (e: React.DragEvent<HTMLTableRowElement>) =>
												handleDragOver(e, r)
										: undefined
								}
								onDragLeave={isDrag ? handleDragLeave : undefined}
							>
								<TableDataCell>
									<Inline gap="3">
										{isDrag ? (
											<Stack justifyContent={"center"}>
												<Box
													padding={"1"}
													bgColor={"accent"}
													color={"primary"}
													borderWidth={"1"}
													borderColor={"accent"}
													rounded="md"
												>
													<Icons.SwitchHorizontal rotate="90" />
												</Box>
											</Stack>
										) : null}
										{ruleGroup.name}
									</Inline>
								</TableDataCell>
								<TableDataCell>
									<Stack alignItems={"stretch"} justifyContent={"center"}>
										{ruleGroup.grouped_rule_items.map((group, gi) => (
											<Stack zIndex="100" key={gi}>
												<Box
													borderColor={"default"}
													borderWidth={"1"}
													bgColor={"accent"}
													padding={"2"}
													rounded="md"
												>
													{group.map((singleRule, si) => (
														<Text key={singleRule.id}>
															{si ? (
																<Box
																	zIndex="100"
																	fontWeight="semibold"
																	borderColor={"default"}
																	borderWidth={"1"}
																	fontSize={"xs"}
																	as="span"
																	padding={"1"}
																	bgColor={"subtle"}
																	rounded="md"
																>
																	OR
																</Box>
															) : (
																""
															)}{" "}
															{singleRule.parameter.display_name}{" "}
															<strong>
																{singleRule.parameter.operator.name}
															</strong>{" "}
															{Array.isArray(singleRule.right_operand_value)
																? singleRule.right_operand_value.join(", ")
																: singleRule.right_operand_value}
														</Text>
													))}
												</Box>

												{ruleGroup.grouped_rule_items.length - 1 !== gi && (
													<Box textAlign={"center"}>
														<Inline
															alignItems={"stretch"}
															justifyContent={"center"}
															height={"12"}
															gap={"2"}
															position="relative"
														>
															<Box
																position="absolute"
																top="0"
																bottom="0"
																style={{ width: "1px", zIndex: -1 }}
																backgroundColor={"emphasis"}
																opacity="50"
															/>

															<Stack justifyContent={"center"}>
																<Box
																	fontWeight="semibold"
																	borderColor={"default"}
																	borderWidth={"1"}
																	fontSize={"xs"}
																	as="span"
																	padding={"1"}
																	bgColor={"subtle"}
																	rounded="md"
																>
																	AND
																</Box>
															</Stack>
														</Inline>
													</Box>
												)}
											</Stack>
										))}
									</Stack>
								</TableDataCell>
								<TableDataCell>
									<Text>
										{joinAttributes(
											ruleGroup.users.length
												? joinAttributes(
														...ruleGroup.users.map((user) => user.name)
													)
												: undefined,
											ruleGroup.teams.length
												? joinAttributes(
														...ruleGroup.teams.map(
															(team) =>
																`${team.name} (${team?.users?.map((user) => user.name).join(",")}) `
														)
													)
												: undefined
										)}
									</Text>
								</TableDataCell>
								{isDrag ? null : (
									<TableDataCell>
										<Box>
											<Dropdown alignRight alignItems={"start"}>
												<Button level="tertiary" as={Dropdown.Toggle} size="sm">
													<Icons.DotsVertical />
												</Button>
												<Dropdown.Menu>
													<AddRuleGroupButton
														onSucces={() => {
															refresh()
														}}
														editableData={ruleGroup}
														title="Edit"
														isEditing={true}
													/>
													<Component initialState={false}>
														{({ state, setState }) => {
															return (
																<>
																	<Button onClick={() => setState(true)}>
																		<Inline alignItems={"center"}>
																			<Icons.Trash /> Delete
																		</Inline>
																	</Button>

																	<Dialog
																		open={state}
																		onClose={() => setState(false)}
																		sm
																	>
																		<Dialog.Header>
																			<Dialog.Title>
																				Delete Rule Set
																			</Dialog.Title>
																		</Dialog.Header>
																		<Form<unknown>
																			onSubmit={withServerErrors(async () => {
																				const resp = await xhr.delete<{
																					message: string
																				}>(`/routing-rules/${ruleGroup.id}`)
																				refresh()
																				setState(false)
																				showSnackbar(resp.data.message)
																			})}
																			subscription={{
																				submitting: true,
																			}}
																		>
																			{({ submitting, handleSubmit }) => (
																				<form
																					noValidate
																					onSubmit={handleSubmit}
																				>
																					<Dialog.Body>
																						<Text>
																							This action will permanently
																							remove the selected lead
																							assignment rule set and its rules.
																						</Text>
																					</Dialog.Body>
																					<Dialog.Footer>
																						<Stack gap="4">
																							<SubmissionError />
																							<Inline gap="4">
																								<Button
																									type="submit"
																									disabled={submitting}
																									status="warning"
																								>
																									{submitting
																										? "deleting..."
																										: "I understand, delete it."}
																								</Button>
																								<Button
																									onClick={() =>
																										setState(false)
																									}
																									disabled={submitting}
																								>
																									Cancel
																								</Button>
																							</Inline>
																						</Stack>
																					</Dialog.Footer>
																				</form>
																			)}
																		</Form>
																	</Dialog>
																</>
															)
														}}
													</Component>
												</Dropdown.Menu>
											</Dropdown>
										</Box>
									</TableDataCell>
								)}
							</Box>
						))}
					</tbody>
				}
			/>
			{isDrag ? (
				<>
					<Divider sm />
					<Inline justifyContent={"end"} gap={"3"}>
						<Button onClick={onCancel}>Cancel</Button>
						<Button level="primary" status="primary" onClick={handelSubmit}>
							Submit
						</Button>
					</Inline>
				</>
			) : null}
		</Stack>
	)
}

function RulesSetReorderDailogBox({
	onSucces,
	onCancel,
	open,
	editableData,
}: {
	open: boolean
	onCancel: () => void
	onSucces: () => void
	editableData: TAppliedRules
}) {
	return (
		<Dialog
			open={open}
			onClose={onCancel}
			title={
				<Stack gap={"2"}>
					<Heading fontSize={"lg"}>Re-arrangement</Heading>
					<Text fontSize={"sm"} fontWeight="normal" color={"muted"}>
						Re-arrange ruleset for better distribution of leads
					</Text>
				</Stack>
			}
		>
			<Dialog.Body>
				<DistributionRulesSetTable
					refresh={onSucces}
					appliedRules={editableData}
					onCancel={onCancel}
					isDrag={true}
				/>
			</Dialog.Body>
		</Dialog>
	)
}
